import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { mockInterviewAPI, cvAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { 
  Mic, ArrowLeft, RefreshCw, CheckCircle, 
  XCircle, Lightbulb, Star, ChevronRight, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const MockInterviewPage = () => {
  const navigate = useNavigate();
  const { isSubscribed } = useAuth();
  const [cvs, setCvs] = useState([]);
  const [selectedCvId, setSelectedCvId] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [difficulty, setDifficulty] = useState('medium');
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [loading, setLoading] = useState(false);
  const [interviewStarted, setInterviewStarted] = useState(false);
  const [completedAnswers, setCompletedAnswers] = useState([]);

  useEffect(() => {
    fetchCVs();
  }, []);

  const fetchCVs = async () => {
    try {
      const res = await cvAPI.list();
      setCvs(res.data);
      if (res.data.length > 0) {
        setSelectedCvId(res.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch CVs:', error);
    }
  };

  const startInterview = async () => {
    if (!jobTitle.trim()) {
      toast.error('Please enter a job title');
      return;
    }
    
    setLoading(true);
    try {
      const res = await mockInterviewAPI.start({
        job_title: jobTitle,
        cv_id: selectedCvId || null,
        difficulty
      });
      setQuestions(res.data.questions || []);
      setInterviewStarted(true);
      setCurrentQuestionIndex(0);
      setCompletedAnswers([]);
      setSelectedAnswer(null);
      setShowFeedback(false);
      toast.success('Interview started! Choose the best answer.');
    } catch (error) {
      toast.error('Failed to start interview');
    } finally {
      setLoading(false);
    }
  };

  const selectAnswer = (choice) => {
    setSelectedAnswer(choice);
    setShowFeedback(true);
    setCompletedAnswers([...completedAnswers, {
      question: questions[currentQuestionIndex].question,
      selectedChoice: choice,
      score: choice.score
    }]);
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer(null);
      setShowFeedback(false);
    }
  };

  const restartInterview = () => {
    setInterviewStarted(false);
    setQuestions([]);
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setShowFeedback(false);
    setCompletedAnswers([]);
  };

  const getScoreColor = (score) => {
    if (score >= 8) return 'text-emerald-600 bg-emerald-100 border-emerald-200';
    if (score >= 5) return 'text-amber-600 bg-amber-100 border-amber-200';
    return 'text-red-600 bg-red-100 border-red-200';
  };

  const getChoiceStyle = (choice) => {
    if (!showFeedback) {
      return 'border-slate-200 hover:border-slate-400 hover:bg-slate-50';
    }
    if (choice.id === selectedAnswer?.id) {
      if (choice.score >= 8) return 'border-emerald-500 bg-emerald-50';
      if (choice.score >= 5) return 'border-amber-500 bg-amber-50';
      return 'border-red-500 bg-red-50';
    }
    if (choice.score >= 8 && selectedAnswer?.score < 8) {
      return 'border-emerald-300 bg-emerald-50/50'; // Show best answer
    }
    return 'border-slate-200 opacity-60';
  };

  const calculateAverageScore = () => {
    if (completedAnswers.length === 0) return 0;
    const total = completedAnswers.reduce((acc, a) => acc + a.score, 0);
    return Math.round((total / completedAnswers.length) * 10);
  };

  if (!isSubscribed) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-lg">
          <Mic className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Interview Practice</h2>
          <p className="text-slate-600 mb-6">Learn what interviewers want to hear. Subscribe to access this feature.</p>
          <Button onClick={() => navigate('/subscribe')} className="bg-amber-500 hover:bg-amber-600">
            Subscribe Now - R25 for 30 Days
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Interview Practice</h1>
            <p className="text-sm text-slate-500">Learn what answers impress interviewers</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 lg:p-8">
        {!interviewStarted ? (
          /* Setup Screen */
          <div className="bg-white rounded-2xl border border-slate-200 p-6 lg:p-8">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mic className="w-10 h-10 text-purple-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Practice Common Interview Questions</h2>
              <p className="text-slate-600">Choose the best answer and learn what interviewers are looking for</p>
            </div>

            {/* Info Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-blue-800 font-medium">How it works</p>
                <p className="text-sm text-blue-700">
                  You'll see common interview questions with 4 possible answers. Pick the one you think is best, 
                  then see instant feedback on why certain answers work better than others.
                </p>
              </div>
            </div>

            <div className="space-y-6 max-w-md mx-auto">
              {/* Job Title */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  What position are you interviewing for?
                </label>
                <input
                  type="text"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  placeholder="e.g., Administrative Assistant"
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                  data-testid="job-title-input"
                />
              </div>

              {/* CV Selection */}
              {cvs.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Select your CV (optional)
                  </label>
                  <select
                    value={selectedCvId}
                    onChange={(e) => setSelectedCvId(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                    data-testid="cv-select"
                  >
                    <option value="">No CV selected</option>
                    {cvs.map(cv => (
                      <option key={cv.id} value={cv.id}>
                        {cv.title || cv.personal_details?.full_name || 'Untitled CV'}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <Button
                onClick={startInterview}
                disabled={loading || !jobTitle.trim()}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white py-6 text-lg"
                data-testid="start-interview-btn"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                    Generating Your Questions...
                  </>
                ) : (
                  <>
                    <Mic className="w-5 h-5 mr-2" />
                    Start Practice
                  </>
                )}
              </Button>
              
              {loading && (
                <p className="text-center text-sm text-slate-500 mt-3">
                  AI is creating custom questions and realistic answers for {jobTitle}. This takes about 10-15 seconds...
                </p>
              )}
            </div>
          </div>
        ) : (
          /* Interview Screen */
          <div className="space-y-6">
            {/* Progress */}
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-600">
                  Question {currentQuestionIndex + 1} of {questions.length}
                </span>
                <span className="text-sm text-slate-500">{jobTitle}</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full">
                <div
                  className="h-2 bg-purple-600 rounded-full transition-all"
                  style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
                />
              </div>
            </div>

            {/* Question Card */}
            {questions[currentQuestionIndex] && (
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mic className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <span className="text-xs font-medium text-purple-600 uppercase tracking-wider">
                      {questions[currentQuestionIndex].category}
                    </span>
                    <p className="text-lg font-semibold text-slate-900 mt-1">
                      {questions[currentQuestionIndex].question}
                    </p>
                    {questions[currentQuestionIndex].tip && (
                      <p className="text-sm text-slate-500 mt-2 flex items-center gap-1">
                        <Lightbulb className="w-4 h-4" />
                        Tip: {questions[currentQuestionIndex].tip}
                      </p>
                    )}
                  </div>
                </div>

                {/* Multiple Choice Answers */}
                <div className="space-y-3">
                  {questions[currentQuestionIndex].choices?.map((choice) => (
                    <button
                      key={choice.id}
                      onClick={() => !showFeedback && selectAnswer(choice)}
                      disabled={showFeedback}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${getChoiceStyle(choice)}`}
                      data-testid={`choice-${choice.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                          showFeedback && choice.id === selectedAnswer?.id 
                            ? getScoreColor(choice.score)
                            : showFeedback && choice.score >= 8 && selectedAnswer?.score < 8
                              ? 'bg-emerald-100 text-emerald-600 border border-emerald-200'
                              : 'bg-slate-100 text-slate-600'
                        }`}>
                          {choice.id}
                        </span>
                        <div className="flex-1">
                          <p className="text-slate-700">{choice.text}</p>
                          
                          {/* Show feedback after selection */}
                          {showFeedback && (
                            <div className={`mt-2 text-sm flex items-center gap-2 ${
                              choice.score >= 8 ? 'text-emerald-600' : 
                              choice.score >= 5 ? 'text-amber-600' : 'text-red-600'
                            }`}>
                              {choice.score >= 8 ? (
                                <CheckCircle className="w-4 h-4" />
                              ) : choice.score >= 5 ? (
                                <AlertCircle className="w-4 h-4" />
                              ) : (
                                <XCircle className="w-4 h-4" />
                              )}
                              <span>{choice.feedback}</span>
                              <span className="ml-auto font-bold">{choice.score}/10</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Next Button */}
                {showFeedback && (
                  <div className="flex gap-3 pt-6 mt-6 border-t border-slate-100">
                    {currentQuestionIndex < questions.length - 1 ? (
                      <Button
                        onClick={nextQuestion}
                        className="flex-1 bg-purple-600 hover:bg-purple-700"
                        data-testid="next-question-btn"
                      >
                        Next Question
                        <ChevronRight className="w-5 h-5 ml-2" />
                      </Button>
                    ) : (
                      <Button
                        onClick={restartInterview}
                        className="flex-1 bg-slate-900 hover:bg-slate-800"
                        data-testid="finish-interview-btn"
                      >
                        <RefreshCw className="w-5 h-5 mr-2" />
                        Practice Again
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Interview Summary (when complete) */}
            {currentQuestionIndex === questions.length - 1 && showFeedback && (
              <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-2xl p-6 text-white">
                <h3 className="text-xl font-bold mb-2">Practice Complete!</h3>
                <p className="text-purple-100 mb-4">
                  You answered {completedAnswers.length} questions. Keep practicing to improve!
                </p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-white/20 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold">
                      {calculateAverageScore()}%
                    </p>
                    <p className="text-sm text-purple-100">Avg Score</p>
                  </div>
                  <div className="bg-white/20 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold">{completedAnswers.filter(a => a.score >= 8).length}</p>
                    <p className="text-sm text-purple-100">Great Answers</p>
                  </div>
                  <div className="bg-white/20 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold">{completedAnswers.length}</p>
                    <p className="text-sm text-purple-100">Questions</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default MockInterviewPage;
